import React, { useEffect } from 'react';
import { Box } from '@mui/material';
import { MapContainer, Marker, TileLayer, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import { RemoveAttributionControl } from './RemoveAttributionControl';

const DEFAULT_CENTER: [number, number] = [24.7136, 46.6753];

const defaultMarkerIcon = L.icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});

L.Marker.prototype.options.icon = defaultMarkerIcon;

type LocationMapPickerProps = {
  latitude?: number;
  longitude?: number;
  onLocationChange: (latitude: number, longitude: number) => void;
  height?: number | string;
};

function MapClickHandler({
  onLocationChange,
}: {
  onLocationChange: (latitude: number, longitude: number) => void;
}) {
  useMapEvents({
    click(event) {
      onLocationChange(event.latlng.lat, event.latlng.lng);
    },
  });
  return null;
}

function FlyToPosition({ position }: { position: [number, number] | null }) {
  const map = useMap();

  useEffect(() => {
    if (position) {
      map.flyTo(position, Math.max(map.getZoom(), 14), { duration: 0.35 });
    }
  }, [position, map]);

  return null;
}

export function LocationMapPicker({
  latitude,
  longitude,
  onLocationChange,
  height = 280,
}: LocationMapPickerProps) {
  const hasPosition =
    latitude != null && longitude != null && !Number.isNaN(latitude) && !Number.isNaN(longitude);
  const position: [number, number] | null = hasPosition ? [latitude!, longitude!] : null;
  const center = position ?? DEFAULT_CENTER;

  return (
    <Box
      sx={{
        height,
        width: '100%',
        borderRadius: '10px',
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <MapContainer
        center={center}
        zoom={position ? 15 : 12}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom
        attributionControl={false}
      >
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        <RemoveAttributionControl />
        <MapClickHandler onLocationChange={onLocationChange} />
        <FlyToPosition position={position} />
        {position && <Marker position={position} />}
      </MapContainer>
    </Box>
  );
}
